import { useCallback, useEffect, useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import InputBase from '@mui/material/InputBase';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import { usePermissions } from 'src/auth/hooks/use-permissions';
import Iconify from 'src/components/iconify';
import type { NavProps } from 'src/components/nav-section/types';
import { useRouter } from 'src/routes/hooks';

import { buildSearchItems, searchItems, type SearchPageAccess } from './global-search-data';

// ----------------------------------------------------------------------

type Props = {
  data: NavProps['data'];
};

const visuallyHidden = {
  width: 1,
  height: 1,
  p: 0,
  m: -1,
  position: 'absolute',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

function HighlightedText({ text, query }: { text: string; query: string }) {
  const matches = query.trim() ? match(text, query, { insideWords: true }) : [];
  const parts = parse(text, matches);

  return (
    <>
      {parts.map((part, index) => (
        <Box
          component="span"
          data-testid={part.highlight ? 'search-highlight' : undefined}
          key={`${part.text}-${index}`}
          sx={{ color: part.highlight ? 'secondary.main' : 'inherit' }}
        >
          {part.text}
        </Box>
      ))}
    </>
  );
}

// ----------------------------------------------------------------------

export default function GlobalSearch({ data }: Props) {
  const router = useRouter();
  const { checkGenericFeature, checkGenericModule } = usePermissions();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const shortcutLabel = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent) ? '⌘K' : 'Ctrl K';

  const canAccessSearchPage = useCallback(
    (access: SearchPageAccess) =>
      (!access.feature || checkGenericFeature(access.feature)) &&
      access.modules.some((module) => checkGenericModule(module, access.action)),
    [checkGenericFeature, checkGenericModule]
  );

  const items = useMemo(() => buildSearchItems(data, canAccessSearchPage), [canAccessSearchPage, data]);

  const filteredItems = useMemo(() => searchItems(items, query), [items, query]);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  const handleNavigate = useCallback(
    (path: string) => {
      handleClose();
      router.push(path);
    },
    [handleClose, router]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === 'k') {
        event.preventDefault();
        handleOpen();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleOpen]);

  return (
    <>
      <ButtonBase
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-keyshortcuts="Control+K Meta+K"
        aria-label="Pesquisar páginas"
        onClick={handleOpen}
        sx={{
          width: { xs: 156, sm: 180 },
          height: 40,
          gap: 0.75,
          px: 1.25,
          color: 'text.secondary',
          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
          borderRadius: 1.5,
          transition: (theme) => theme.transitions.create(['background-color', 'color']),
          '&:hover': {
            color: 'text.primary',
            bgcolor: (theme) => alpha(theme.palette.grey[500], 0.16),
          },
        }}
      >
        <Iconify icon="eva:search-fill" width={20} />

        <Typography component="span" variant="body2" sx={{ flexGrow: 1, textAlign: 'left' }}>
          Procurar
        </Typography>

        <Box
          component="kbd"
          sx={{
            minWidth: shortcutLabel === '⌘K' ? 28 : 42,
            px: 0.75,
            py: 0.25,
            color: 'text.primary',
            bgcolor: 'background.paper',
            border: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`,
            borderRadius: 0.75,
            boxShadow: (theme) => `0 1px 2px ${alpha(theme.palette.grey[900], 0.08)}`,
            fontFamily: 'inherit',
            fontSize: 12,
            fontWeight: 700,
            lineHeight: '20px',
            textAlign: 'center',
          }}
        >
          {shortcutLabel}
        </Box>
      </ButtonBase>

      <Dialog
        aria-labelledby="global-search-dialog-title"
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={handleClose}
        BackdropProps={{
          sx: {
            bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
            backdropFilter: 'blur(2px)',
          },
        }}
        PaperProps={{
          sx: {
            width: 1,
            maxHeight: { xs: 'calc(100vh - 32px)', sm: 640 },
            m: 2,
            overflow: 'hidden',
          },
        }}
        sx={{
          zIndex: (theme) => theme.zIndex.tooltip + 1,
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            pt: { xs: 0, sm: 8 },
          },
        }}
      >
        <Typography id="global-search-dialog-title" sx={visuallyHidden}>
          Pesquisar páginas
        </Typography>

        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            minHeight: 72,
            px: { xs: 2, sm: 3 },
            borderBottom: (theme) => `solid 1px ${theme.palette.divider}`,
          }}
        >
          <Iconify icon="eva:search-fill" width={24} sx={{ color: 'text.secondary', flexShrink: 0 }} />

          <InputBase
            autoFocus
            fullWidth
            placeholder="Pesquisar..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            inputProps={{ 'aria-label': 'Pesquisar páginas' }}
            sx={{ typography: 'h6', fontWeight: 'fontWeightRegular' }}
          />

          <Box
            component="kbd"
            sx={{
              px: 0.75,
              py: 0.25,
              color: 'text.secondary',
              bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12),
              borderRadius: 0.75,
              fontFamily: 'inherit',
              fontSize: 12,
              fontWeight: 700,
              lineHeight: '20px',
            }}
          >
            Esc
          </Box>
        </Stack>

        <Box sx={{ maxHeight: { xs: 'calc(100vh - 104px)', sm: 568 }, overflowY: 'auto', px: 2, py: 1.5 }}>
          {filteredItems.length > 0 ? (
            <List disablePadding aria-label="Páginas disponíveis">
              {filteredItems.map((item) => (
                <ListItem disablePadding key={item.path}>
                  <ListItemButton
                    onClick={() => handleNavigate(item.path)}
                    sx={{
                      minHeight: 62,
                      px: 1,
                      py: 1,
                      gap: 2,
                      borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
                      borderRadius: 0.75,
                      '&:hover': {
                        bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08),
                      },
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                      <Typography variant="subtitle2" noWrap>
                        <HighlightedText text={item.title} query={query} />
                      </Typography>

                      <Typography variant="caption" noWrap sx={{ display: 'block', color: 'text.secondary' }}>
                        <HighlightedText text={item.path} query={query} />
                      </Typography>
                    </Box>

                    <Box
                      component="span"
                      sx={{
                        maxWidth: { xs: 112, sm: 180 },
                        flexShrink: 0,
                        px: 0.75,
                        py: 0.25,
                        color: 'text.primary',
                        bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12),
                        borderRadius: 0.75,
                        typography: 'caption',
                        fontWeight: 'fontWeightBold',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.section}
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ minHeight: 220, textAlign: 'center' }}>
              <Iconify icon="eva:search-outline" width={48} sx={{ color: 'text.disabled' }} />

              <Typography variant="subtitle1">Nenhuma página encontrada</Typography>

              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Tente buscar por outro nome ou caminho.
              </Typography>
            </Stack>
          )}
        </Box>
      </Dialog>
    </>
  );
}
